import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Folder, ChevronRight, Home } from 'lucide-react';
import { SharePointFile } from '@/hooks/useSharePointFiles';

interface MoveToFolderDialogProps {
  open: boolean;
  onClose: () => void;
  onMove: (targetPath: string) => void;
  currentPath: string;
  files: SharePointFile[];
}

export function MoveToFolderDialog({ 
  open, 
  onClose, 
  onMove, 
  currentPath,
  files 
}: MoveToFolderDialogProps) {
  const [selectedPath, setSelectedPath] = useState('/');
  const [navigationHistory, setNavigationHistory] = useState<string[]>(['/']);

  useEffect(() => {
    if (open) {
      setSelectedPath('/');
      setNavigationHistory(['/']);
    }
  }, [open]);

  const folders = files.filter(f => f.folder);
  
  const handleFolderClick = (folderName: string) => {
    const newPath = selectedPath === '/' 
      ? `/${folderName}` 
      : `${selectedPath}/${folderName}`;
    setSelectedPath(newPath);
    setNavigationHistory([...navigationHistory, newPath]);
  };

  const navigateToPath = (path: string) => {
    setSelectedPath(path);
    const index = navigationHistory.indexOf(path);
    if (index !== -1) {
      setNavigationHistory(navigationHistory.slice(0, index + 1));
    }
  };

  const handleMove = () => {
    if (selectedPath !== currentPath) {
      onMove(selectedPath);
      onClose();
    }
  };

  const pathParts = selectedPath.split('/').filter(Boolean);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Verplaats naar map</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Breadcrumb navigatie */}
          <div className="flex items-center gap-2 text-sm flex-wrap">
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => navigateToPath('/')}
              className={selectedPath === '/' ? 'bg-accent' : ''}
            >
              <Home className="h-4 w-4" />
            </Button>
            {pathParts.map((part, idx) => {
              const path = '/' + pathParts.slice(0, idx + 1).join('/');
              return (
                <div key={idx} className="flex items-center gap-2">
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => navigateToPath(path)}
                    className={path === selectedPath ? 'bg-accent' : ''}
                  >
                    {part}
                  </Button>
                </div>
              );
            })}
          </div>

          {/* Huidige locatie */}
          <div className="p-3 bg-muted rounded-lg">
            <p className="text-sm text-muted-foreground">Huidige locatie:</p>
            <p className="font-medium">{currentPath}</p>
          </div>

          {/* Folder lijst */}
          <ScrollArea className="h-64 border rounded-lg p-2">
            {folders.length === 0 ? (
              <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
                Geen submappen beschikbaar
              </div>
            ) : (
              <div className="space-y-1">
                {folders.map((folder) => (
                  <Button
                    key={folder.id}
                    variant="ghost"
                    className="w-full justify-start"
                    onClick={() => handleFolderClick(folder.name)}
                  >
                    <Folder className="h-4 w-4 mr-2 text-blue-500" />
                    {folder.name}
                    <ChevronRight className="h-4 w-4 ml-auto" />
                  </Button>
                ))}
              </div>
            )}
          </ScrollArea>

          {/* Geselecteerde doelmap */}
          <div className="p-3 bg-primary/5 border border-primary/20 rounded-lg">
            <p className="text-sm text-muted-foreground mb-1">Verplaatsen naar:</p>
            <p className="font-medium">{selectedPath}</p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Annuleren
          </Button>
          <Button 
            onClick={handleMove}
            disabled={selectedPath === currentPath}
          >
            Verplaatsen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
